interface ProfileFields {
  uid: string | null;
  nickname: string;
  email: string | null;
  profileComplete: boolean;
}
interface FlowRun { id: string; phase: string; dateKey: string; dayEndAt: string }
interface FlowSubmission { status: string; firstSubmittedAt?: string; supplementDeadline?: string | null }

export function profileIsComplete(person: ProfileFields): boolean {
  return person.profileComplete
    && /^[0-9]{10}$/.test(person.uid ?? '')
    && Array.from(person.nickname.trim()).length >= 1
    && Array.from(person.nickname.trim()).length <= 20
    && !/[\u0000-\u001f\u007f-\u009f]/u.test(person.nickname)
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email?.trim() ?? '')
    && (person.email?.trim().length ?? 0) <= 254;
}

export function requiresProfile(page: string, person: ProfileFields): boolean {
  return (page === 'evidence' || page === 'guide') && !profileIsComplete(person);
}

export function selectFlowRun<T extends FlowRun>(runs: T[], selectedId: string | null, currentRun: T | null, page = 'result'): T | null {
  // An explicit URL must never silently attach evidence to a different run.
  if (selectedId) return runs.find(run => run.id === selectedId) ?? null;
  if (currentRun?.phase === 'SETTLED' || (page === 'play' && currentRun)) return currentRun;
  return [...runs].filter(run => run.phase === 'SETTLED').sort((a, b) => b.dateKey.localeCompare(a.dateKey))[0] ?? currentRun;
}

export function activityNow(clock: { now: string; observedAt: number; frozen?: boolean }, monotonicNow: number): number {
  return Date.parse(clock.now) + (clock.frozen ? 0 : Math.max(0, monotonicNow - clock.observedAt));
}

export function evidenceAccess(run: FlowRun | undefined | null, submission: FlowSubmission | undefined, now: number) {
  if (!run || run.phase !== 'SETTLED') return { canUpload: false, state: 'no_run', deadline: null } as const;
  if (submission?.status === 'supplement_requested') {
    const deadline = submission.supplementDeadline ?? null;
    const timelyOriginal = Date.parse(submission.firstSubmittedAt ?? '') < Date.parse(run.dayEndAt);
    const canUpload = timelyOriginal && Boolean(deadline) && now < Date.parse(deadline!);
    return { canUpload, state: canUpload ? 'supplement' : 'supplement_expired', deadline } as const;
  }
  if (submission) return { canUpload: false, state: 'submitted', deadline: run.dayEndAt } as const;
  const canUpload = now < Date.parse(run.dayEndAt);
  return { canUpload, state: canUpload ? 'initial' : 'initial_expired', deadline: run.dayEndAt } as const;
}

export function historyDayState(day: number, current: { dateKey: string; dayNumber: number }): 'past' | 'today' | 'future' {
  if (current.dateKey === 'before_start') return 'future';
  if (current.dateKey === 'after_end' || day < current.dayNumber) return 'past';
  return day === current.dayNumber ? 'today' : 'future';
}

export function deadlineLabel(value: number | string, locale: string, timezone: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', timeZoneName: 'short',
  }).format(new Date(value));
}
